import { environment } from '../../../environments/environment';
import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { LoginService } from '../../login/login.service';
import { SnackbarService } from '../../shared/services/snackbar.service';
import {
    ACCESS_KEYS,
    LocalStorageService,
} from '../../shared/services/local-storage.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
    resetPasswordForm!: FormGroup;

    // First-time setup = the user has never set their own password (e.g. signed in with
    // Google, or never used a temporary password). In this mode we do NOT ask for a current
    // password (they have none) and the dialog cannot be dismissed.
    isFirstTimeSetup = false;
    isSubmitting = false;
    errorMessage = '';

    hideCurrent = true;
    hideNew = true;
    hideConfirm = true;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ResetPasswordComponent>,
        private snackBar: SnackbarService,
        private http: HttpClient,
        private authService: AuthService,
        private loginService: LoginService,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnInit() {
        this.isFirstTimeSetup = !this.authService.isPasswordUpdated();
        // Block ESC / backdrop dismissal when a password must be set.
        this.dialogRef.disableClose = this.isFirstTimeSetup;

        const controls: { [key: string]: unknown } = {
            newPassword: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.maxLength(64),
                ],
            ],
            retypeNewPassword: ['', [Validators.required]],
        };
        if (!this.isFirstTimeSetup) {
            controls['currentPassword'] = ['', [Validators.required]];
        }

        this.resetPasswordForm = this.fb.group(controls, {
            validators: this.passwordsMatchValidator,
        });

        // Clear a stale server error (e.g. "current password not matching") as soon as the
        // user starts correcting their input.
        this.resetPasswordForm.valueChanges.subscribe(() => {
            if (this.errorMessage) {
                this.errorMessage = '';
            }
        });
    }

    // Group-level validator: new password and its confirmation must match.
    private passwordsMatchValidator(
        group: AbstractControl,
    ): ValidationErrors | null {
        const newPassword = group.get('newPassword')?.value;
        const retype = group.get('retypeNewPassword')?.value;
        if (newPassword && retype && newPassword !== retype) {
            return { passwordMismatch: true };
        }
        return null;
    }

    get f() {
        return this.resetPasswordForm.controls;
    }

    onSubmit() {
        this.errorMessage = '';
        if (this.resetPasswordForm.invalid) {
            this.resetPasswordForm.markAllAsTouched();
            return;
        }

        this.isSubmitting = true;
        const currentPassword = this.isFirstTimeSetup
            ? ''
            : this.resetPasswordForm.value.currentPassword;

        this.resetPassword(
            currentPassword,
            this.resetPasswordForm.value.newPassword,
        )
            .then(() => {
                this.snackBar.showMessageWithDelay(
                    'Password updated. Please sign in again.',
                    4000,
                );
                this.dialogRef.close(true);
                // Re-authenticate so the new session carries the updated state.
                this.loginService.logout();
            })
            .catch((err) => {
                this.isSubmitting = false;
                this.errorMessage =
                    err?.error?.response ||
                    err?.error?.message ||
                    'Something went wrong. Please try again.';
            });
    }

    onCancel() {
        if (this.isFirstTimeSetup) {
            return; // Forced — cannot be cancelled.
        }
        this.dialogRef.close(false);
    }

    private resetPassword(
        oldPassword: string,
        newPassword: string,
    ): Promise<any> {
        const email = this.localStorageService.getItem(
            ACCESS_KEYS.BLUMEPRESS_ID,
        );
        return firstValueFrom(
            this.http.put(`${environment.url}api/v1/accounts/resetPassword`, {
                email,
                oldPassword,
                newPassword,
            }),
        );
    }
}
